import isUndefined from 'lodash.isundefined';
import isNull from 'lodash.isnull';
import isString from 'lodash.isstring';

/* eslint-disable no-use-before-define */
export default class StorageController {
    constructor(options) {
        this.defaults = {
            localStorageAvailable: null,
            sessionStorageAvailable: null,
            defaultLifetime: 14, // 14 days
        };

        this.settings = null;

        this.responseText = {
            success: 'success',
            fail: 'fail',
        };

        this.init(options);
    }

    /**
     * Initialise
     * - Check for availability of localStorage & sessionStorage APIs
     */
    init = (options) => {
        this.settings = Object.assign(this.defaults, options);

        this.settings.localStorageAvailable = this._isLocalStorageAvailable();
        this.settings.sessionStorageAvailable = this._isSessionStorageAvailable();
    };

    /**
     * Save data item to storage
     *
     * itemKey: string,
     * itemValue: any type of JS data - will be stringified,
     * [options]: object containing optional settings below:
     *    - mode: 'localStorage', 'sessionStorage', or 'cookie' - default 'localStorage',
     *    - customExpiryDate: date object
     */
    saveItem = (itemKey, itemValue, itemOptions) => {
        const options = itemOptions || {};
        let expiryDate = '';
        let formattedValue = '';
        const { customExpiryDate, mode } = options;
        const response = { status: this.responseText.fail };

        expiryDate = this._generateExpiryDate(customExpiryDate);

        // Clear expiryDate for sessions
        if (mode === 'sessionStorage') {
            expiryDate = null;
        }

        const usedMode = this._useMode(mode);

        switch (usedMode) {
            case 'localStorage':
                formattedValue = this._formatData(itemValue, expiryDate);
                response.status = this._saveLocalStorage(itemKey, formattedValue);
                break;
            case 'sessionStorage':
                formattedValue = this._formatData(itemValue);
                response.status = this._saveSessionStorage(itemKey, formattedValue);
                break;
            case 'cookie':
                formattedValue = this._formatData(itemValue);
                response.status = this._saveCookie(itemKey, formattedValue, expiryDate);
                break;
            default:
                formattedValue = this._formatData(itemValue);
                response.status = this._saveCookie(itemKey, formattedValue, expiryDate);
        }

        return response;
    };

    /**
     * Return data item from storage
     *
     * itemKey: string,
     * [options]: object containing optional settings below:
     *    - mode: 'localStorage', 'sessionStorage', or 'cookie' - if null check all 3'
     */

    loadItem = (itemKey, itemOptions) => {
        const options = itemOptions || {};
        let value;
        const { mode } = options;

        // Check if item has expired
        this._isExpired(itemKey);

        if (isUndefined(mode) || isNull(mode)) {
            value = this.checkAllStorageTypes(itemKey);
            return value;
        }

        switch (mode) {
            case 'localStorage':
                return this._loadLocalStorage(itemKey);
            case 'sessionStorage':
                return this._loadSessionStorage(itemKey);
            case 'cookie':
                return this._loadCookie(itemKey);
            default:
                return null;
        }
    };

    checkAllStorageTypes = (itemKey) => {
        let desiredValue = this._loadLocalStorage(itemKey);

        if (!desiredValue) {
            desiredValue = this._loadSessionStorage(itemKey);

            if (!desiredValue) {
                desiredValue = this._loadCookie(itemKey);
            }
        }

        return desiredValue;
    };

    /**
     * Delete data item from storage
     *
     * itemKey: string,
     * [options]: object containing optional settings below:
     *    - mode: 'localStorage', 'sessionStorage', or 'cookie' - default 'localStorage'
     */
    deleteItem = (itemKey, itemOptions) => {
        const options = itemOptions || {};
        const mode = this._useMode(options.mode);

        switch (mode) {
            case 'localStorage':
                this._deleteLocalStorage(itemKey);
                break;
            case 'sessionStorage':
                this._deleteSessionStorage(itemKey);
                break;
            case 'cookie':
                this._deleteCookie(itemKey);
                break;
            default:
                return null;
        }

        return null;
    };

    /**
     * Save to localStorage
     */
    _saveLocalStorage = (itemKey, itemValue) => {
        try {
            localStorage.setItem(itemKey, itemValue);
            return this.responseText.success;
        } catch (e) {
            return this.responseText.fail;
        }
    };

    /**
     * Save to sessionStorage
     */
    _saveSessionStorage = (itemKey, itemValue) => {
        try {
            sessionStorage.setItem(itemKey, itemValue);
            return this.responseText.success;
        } catch (e) {
            return this.responseText.fail;
        }
    };

    /**
     * Save to cookie.
     */
    _saveCookie = (itemKey, itemValue, expiryDate) => {
        // if expiryDateString === '' then it sets a session cookie...
        let expiryDateString = '';
        let formatDate;

        // ... otherwise convert timestamp to a string for the expiry date
        if (!isNull(expiryDate)) {
            formatDate = new Date();
            formatDate.setTime(expiryDate);
            expiryDateString = formatDate.toUTCString();
        }

        try {
            document.cookie = `${encodeURI(itemKey)}=${itemValue}; expires=${expiryDateString}; path=/`;
            return this.responseText.success;
        } catch (e) {
            return this.responseText.fail;
        }
    };

    /**
     * Return saved data from localStorage
     */
    _loadLocalStorage = (itemKey) => {
        const response = localStorage.getItem(itemKey);
        let item = null;

        if (!isNull(response)) {
            try {
                item = JSON.parse(response);
            } catch (e) {
                return null;
            }

            return item.value;
        }

        return null;
    };

    /**
     * Return saved data from sessionStorage
     */
    _loadSessionStorage = (itemKey) => {
        const response = sessionStorage.getItem(itemKey);
        let item = null;

        if (!isNull(response)) {
            try {
                item = JSON.parse(response);
            } catch (e) {
                return null;
            }

            return item.value;
        }

        return null;
    };

    /**
     * Return boolean based on localStorage API's availability
     */
    _isLocalStorageAvailable = () => typeof (window.Storage) !== 'undefined';

    /**
     * Return boolean based on sessionStorage API's availability
     */
    _isSessionStorageAvailable = () => typeof (window.Storage) !== 'undefined';

    /**
     * Return saved data from cookie
     * Ref: https://developer.mozilla.org/en-US/docs/Web/API/document.cookie
     */
    _loadCookie = (itemKey) => {
        let item = {};

        if (!itemKey || !this._cookieHasOwnProperty(itemKey)) {
            return null;
        }

        // eslint-disable-next-line max-len
        const response = decodeURI(document.cookie.replace(new RegExp(`(?:^|.*;\\s*)${encodeURI(itemKey).replace(/[-.+*]/g, '\\$&')}\\s*\\=\\s*((?:[^;](?!;))*[^;]?).*`), '$1'));

        try {
            item = JSON.parse(response);
            // TODO: Find out why Phantom requires a double parse.
            if (isString(item)) {
                if (this._isStringValidJson(item)) {
                    item = JSON.parse(item);
                } else {
                    item = {
                        value: item,
                    };
                }
            }
        } catch (e) {
            return null;
        }

        return item.value;
    };

    /**
     * check if string is a valid JSON
     */
    _isStringValidJson = (str) => {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    };

    /**
     * Return boolean based on existence of cookie
     * Ref: https://developer.mozilla.org/en-US/docs/Web/API/document.cookie
     */
    _cookieHasOwnProperty = (itemKey) => new RegExp(`(?:^|;\\s*)${encodeURI(itemKey).replace(/[-.+*]/g, '\\$&')}\\s*\\=`).test(document.cookie);

    /**
     * Delete saved data from localStorage
     */
    _deleteLocalStorage = (itemKey) => {
        localStorage.removeItem(itemKey);
    };

    /**
     * Delete saved data from sessionStorage
     */
    _deleteSessionStorage = (itemKey) => {
        sessionStorage.removeItem(itemKey);
    };

    /**
     * Delete saved data from cookie
     * Ref: https://developer.mozilla.org/en-US/docs/Web/API/document.cookie
     */
    _deleteCookie = (itemKey) => {
        const cookieExpiryDate = new Date(0);

        if (!itemKey || !this._cookieHasOwnProperty(itemKey)) {
            return null;
        }

        document.cookie = `${encodeURI(itemKey)}=; expires=${cookieExpiryDate.toUTCString()}; path=/`;

        return true;
    };

    /**
     * Return string of which storage tech should be used
     */
    _useMode = (mode) => {
        let storageMode = mode;

        // default to localStorage
        if (isUndefined(mode) || isNull(mode)) {
            storageMode = 'localStorage';
        }

        // fallback to cookie if APIs unavailable
        if (mode === 'localStorage' && !this.settings.localStorageAvailable) {
            storageMode = 'cookie';
        } else if (mode === 'sessionStorage' && !this.settings.sessionStorageAvailable) {
            storageMode = 'cookie';
        }

        return storageMode;
    };

    /**
     * Return string of data + apply expiry date when available
     */
    _formatData = (data, expiryDate) => {
        const dataObject = {};

        dataObject.value = data;

        // optional field
        if (!isUndefined(expiryDate)) {
            dataObject.expiryDate = expiryDate;
        }

        return JSON.stringify(dataObject);
    };

    /**
     * Return timestamp for expiry date for data
     */
    _generateExpiryDate = (customExpiryDate) => {
        const now = new Date();
        const future = new Date();
        let newDate = null;

        // only accept date object
        if (customExpiryDate instanceof Date) {
            newDate = customExpiryDate.getTime();
        } else if (isNull(customExpiryDate)) {
            newDate = null;
        } else {
            newDate = future.setDate(now.getDate() + this.settings.defaultLifetime);
        }

        return newDate;
    };

    /**
     * Delete an expired localStorage item
     */
    _isExpired = (itemKey) => {
        const nowStamp = new Date().getTime();
        let item = {};

        if (!this.settings.localStorageAvailable) {
            return null;
        }

        item = localStorage[itemKey];

        if (item) {
            item = JSON.parse(item);

            if (!isUndefined(item.expiryDate) && item.expiryDate < nowStamp) {
                this._deleteLocalStorage(itemKey);
                return true;
            }
        }

        return false;
    };
}
